import {
  useState, useRef, useMemo, useEffect,
} from 'react';
import { useApiClient } from '@marageti/z4-lib';
import { User } from '@marageti/z4-sdk/lib/auth';

interface CacheEntry {
  data: User | null;
  loading: boolean;
  error: boolean;
}

export interface UseUserCacheReturn {
  /** Fetch user data from API (or return cached). Triggers re-render on completion. */
  fetchUser: (userId: string) => void;
  /** Get cached user data. Returns undefined if not fetched, null if 404/error. */
  getUserData: (userId: string) => User | null | undefined;
  /** Whether a fetch is currently in-flight for the given userId. */
  isLoadingUser: (userId: string) => boolean;
  /** Whether the last fetch for this userId failed (retryable). */
  hasError: (userId: string) => boolean;
}

/**
 * Hook that provides on-demand user fetching with an in-memory cache.
 * Users are fetched individually when requested (e.g. via a "Get user info" button).
 * Results are cached so subsequent requests for the same userId are instant.
 * Failed fetches (404, 500, network) are flagged with `error: true` so the user can retry.
 */
export const useUserCache = (): UseUserCacheReturn => {
  const apiClient = useApiClient();

  // Single unified state: each entry holds both loading flag and data.
  const [cache, setCache] = useState<Record<string, CacheEntry>>({});

  // Ref mirror -- keeps callbacks stable while reading latest data
  const cacheRef = useRef<Record<string, CacheEntry>>(cache);
  cacheRef.current = cache;

  // AbortController to cancel in-flight requests on unmount
  const abortRef = useRef<AbortController>(new AbortController());
  useEffect(() => () => {
    abortRef.current.abort();
  }, []);

  const fetchUser = (userId: string) => {
    const existing = cacheRef.current[userId];
    // Allow retry if the previous attempt had an error; skip if already loaded or loading
    if (existing && !existing.error) return;

    // Mark as loading
    setCache((prev) => ({
      ...prev,
      [userId]: { data: null, loading: true, error: false },
    }));

    const { signal } = abortRef.current;

    apiClient.authClient.getUser(encodeURIComponent(userId), { signal })
      .then((user) => {
        if (signal.aborted) return;
        setCache((prev) => ({
          ...prev,
          [userId]: { data: user, loading: false, error: false },
        }));
      })
      .catch((err) => {
        if (signal.aborted) return;

        // All errors (including 404) are retryable
        setCache((prev) => ({
          ...prev,
          [userId]: { data: null, loading: false, error: true },
        }));
        // eslint-disable-next-line no-console
        console.error(`Failed to fetch user ${userId}:`, err);
      });
  };

  const getUserData = (userId: string): User | null | undefined => {
    const entry = cacheRef.current[userId];
    if (!entry) return undefined;
    return entry.data;
  };

  const isLoadingUser = (userId: string): boolean => {
    const entry = cacheRef.current[userId];
    return entry?.loading ?? false;
  };

  const hasError = (userId: string): boolean => {
    const entry = cacheRef.current[userId];
    return entry?.error ?? false;
  };

  // Ref-based stable wrapper so the returned object identity never changes,
  // avoiding unnecessary re-renders in consumers that depend on it (e.g. buildColumns).
  const latestRef = useRef<UseUserCacheReturn>({
    fetchUser, getUserData, isLoadingUser, hasError,
  });
  latestRef.current = {
    fetchUser, getUserData, isLoadingUser, hasError,
  };

  const stableCache = useMemo<UseUserCacheReturn>(() => ({
    fetchUser: (...args) => latestRef.current.fetchUser(...args),
    getUserData: (...args) => latestRef.current.getUserData(...args),
    isLoadingUser: (...args) => latestRef.current.isLoadingUser(...args),
    hasError: (...args) => latestRef.current.hasError(...args),
  }), []);

  return stableCache;
};

export default useUserCache;
